/**
 * Connects the analytics module to the running app: one `page_view` per
 * navigation, and the signed-in session's non-identifying attributes.
 *
 * This is the only wiring analytics needs, and it lives in its own service
 * rather than in `AppComponent` for two reasons: the component is already
 * carrying session, SSO and socket concerns, and wiring buried in a large
 * `ngOnInit` is easy to lose in a revert or a merge — at which point the whole
 * analytics module goes quiet with nothing failing to say so.
 *
 * Registered as an app initializer, so the subscriptions exist before the router
 * performs its first navigation and no page view is missed on cold start.
 */
import { inject, Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';

import type { RolesEnum } from 'src/app/features/tests/features/constants';

import { setAnalyticsUser, trackEvent, trackPageView } from './analytics';
import { deepestRouteTitle } from './page-title.strategy';
import { AbilityService } from './services/ability/ability.service';
import { UserService } from './services/user.service';

@Injectable({ providedIn: 'root' })
export class AnalyticsBootstrapService {
  private readonly router = inject(Router);
  private readonly userService = inject(UserService);
  private readonly ability = inject(AbilityService);

  private started = false;
  /**
   * Whether an identity was ever sent. Without this, the `currentUser` stream's
   * initial `null` would queue a pointless "clear the user" command on every
   * cold start.
   */
  private hasIdentity = false;

  /**
   * Idempotent, so a second registration cannot double every page view.
   *
   * Every branch is wrapped: this runs as an app initializer, so an exception
   * here would stop the whole application from starting. Analytics is never
   * worth a blank screen.
   */
  start(): void {
    if (this.started) return;
    this.started = true;

    // Prepares the queue only. The tag itself is fetched lazily, on the first
    // tracked event.
    this.guard(() => this.trackNavigations());
    this.guard(() => this.trackIdentity());
    this.guard(() => this.trackLoadTime());
  }

  /** Runs a step, swallowing anything it throws. */
  private guard(step: () => void): void {
    try {
      step();
    } catch {
      // An analytics failure must stay invisible to the user and to the app.
    }
  }

  /**
   * How long the app took to become usable. GA collects no page-timing of its
   * own, so without this "app load time" simply does not exist in the reports.
   */
  private trackLoadTime(): void {
    // Not every browser exposes the navigation timing entries, and a missing
    // load time is not a reason to fail.
    if (typeof performance?.getEntriesByType !== 'function') return;

    const send = () => {
      this.guard(() => {
        const [entry] = performance.getEntriesByType('navigation') ?? [];
        const navigation = entry as PerformanceNavigationTiming | undefined;
        if (!navigation?.loadEventEnd) return;

        trackEvent('app_load', {
          load_ms: Math.round(navigation.loadEventEnd),
          nav_type: navigation.type,
        });
      });
    };

    if (document.readyState === 'complete') {
      send();
    } else {
      window.addEventListener('load', send, { once: true });
    }
  }

  private trackNavigations(): void {
    this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe((event) => {
        this.guard(() => {
          // The title comes from the route rather than `document.title`, which
          // the router may not have updated yet at this point in the navigation.
          trackPageView(
            event.urlAfterRedirects,
            deepestRouteTitle(this.router.routerState.snapshot),
          );
        });
      });
  }

  private trackIdentity(): void {
    this.userService.currentUser.subscribe((currentUser) => {
      this.guard(() => this.applyIdentity(currentUser));
    });
  }

  private applyIdentity(currentUser: {
    id?: string;
    role?: RolesEnum;
    isMainLab?: boolean;
  } | null): void {
    if (!currentUser) {
      // Only worth sending as a genuine logout, not as the stream's initial
      // empty value.
      if (this.hasIdentity) {
        setAnalyticsUser(null);
        this.hasIdentity = false;
      }
      return;
    }

    // Role and lab only — never a name, an email, or a raw account id.
    setAnalyticsUser({
      userId: currentUser.id,
      role: currentUser.role,
      isMainLab: !!currentUser.isMainLab,
      isSSO: this.ability.isSSO,
    });
    this.hasIdentity = true;
  }
}
